import React from 'react';
import { useNavigate } from 'react-router-dom';
import projects, {
  techStack,
  evaluationCriteria,
  submission,
  roleEvaluation,
} from '../../data/projects';
import {
  Page,
  Hero,
  HeroBadge,
  HeroTitle,
  HeroSubtitle,
  HeroStats,
  StatItem,
  StatValue,
  StatLabel,
  SectionTitle,
  SectionSubtitle,
  ProjectsGrid,
  ProjectCard,
  CardBadge,
  CardTitle,
  CardTagline,
  CardDescription,
  CardFooter,
  ViewButton,
  Section,
  TechGrid,
  TechCard,
  TechCardTitle,
  TechOptions,
  TechOption,
  TechNote,
  EvalGrid,
  EvalCard,
  EvalNumber,
  EvalTitle,
  EvalDescription,
  EvalWeight,
  SubmissionSection,
  SubmissionBox,
  SubReqTitle,
  SubReqDetail,
  SubFormat,
  SubFormatCode,
  SubImportant,
  RoleSection,
  RoleGrid,
  RoleCard,
  RoleTitle,
  RoleSignals,
  RoleDescription,
  RoleStack,
  AiChip,
} from './styles';

export default function Home() {
  const navigate = useNavigate();

  return (
    <Page>
      <Hero>
        <HeroBadge>Engineering Hiring Challenge 2026</HeroBadge>
        <HeroTitle>
          Build Something<br />
          <span>Real.</span>
        </HeroTitle>
        <HeroSubtitle>
          Choose one of two backend-heavy projects. Integrate real APIs. Ship working code.
          Record a video explaining your decisions. We evaluate how you think, not just what you build.
        </HeroSubtitle>
        <HeroStats>
          <StatItem>
            <StatValue>7</StatValue>
            <StatLabel>Days to Build</StatLabel>
          </StatItem>
          <StatItem>
            <StatValue>2</StatValue>
            <StatLabel>Project Options</StatLabel>
          </StatItem>
          <StatItem>
            <StatValue>7+</StatValue>
            <StatLabel>Free Integrations</StatLabel>
          </StatItem>
          <StatItem>
            <StatValue>AI</StatValue>
            <StatLabel>Features Required</StatLabel>
          </StatItem>
        </HeroStats>
      </Hero>

      <Section id="projects">
        <SectionTitle>Choose Your Project</SectionTitle>
        <SectionSubtitle>
          Each project is a real-world engineering challenge. Pick the one that excites you —
          your choice tells us about your interests.
        </SectionSubtitle>
        <ProjectsGrid>
          {projects.map((project) => (
            <ProjectCard
              key={project.id}
              accentColor={project.color}
              glowColor={project.colorGlow}
              onClick={() => navigate(`/project/${project.slug}`)}
            >
              <CardBadge accentColor={project.color}>{project.subtitle}</CardBadge>
              <CardTitle>{project.title}</CardTitle>
              <CardTagline>{project.tagline}</CardTagline>
              <CardDescription>{project.overview.description}</CardDescription>
              <AiChip accentColor={project.color}>
                AI Features Included
              </AiChip>
              <CardFooter>
                <span>{project.integrations.length} Free Integrations</span>
                <ViewButton accentColor={project.color}>
                  View Full Details
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M6 12L10 8L6 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </ViewButton>
              </CardFooter>
            </ProjectCard>
          ))}
        </ProjectsGrid>
      </Section>

      <Section id="tech-stack">
        <SectionTitle>{techStack.title}</SectionTitle>
        <SectionSubtitle>{techStack.description}</SectionSubtitle>
        <TechGrid>
          {[techStack.backend, techStack.frontend, techStack.database].map((tech) => (
            <TechCard key={tech.title}>
              <TechCardTitle>{tech.title}</TechCardTitle>
              <TechOptions>
                {tech.options.map((opt) => (
                  <TechOption key={opt}>{opt}</TechOption>
                ))}
              </TechOptions>
              <TechNote>{tech.note}</TechNote>
            </TechCard>
          ))}
        </TechGrid>
      </Section>

      <Section id="evaluation">
        <SectionTitle>How We Evaluate</SectionTitle>
        <SectionSubtitle>
          Six dimensions. No trick questions. We want to see how you think, debug, and make decisions.
        </SectionSubtitle>
        <EvalGrid>
          {evaluationCriteria.map((criteria) => (
            <EvalCard key={criteria.id}>
              <EvalNumber>{String(criteria.id).padStart(2, '0')}</EvalNumber>
              <EvalTitle>{criteria.title}</EvalTitle>
              <EvalDescription>{criteria.description}</EvalDescription>
              <EvalWeight weight={criteria.weight}>{criteria.weight} Impact</EvalWeight>
            </EvalCard>
          ))}
        </EvalGrid>
      </Section>

      <Section id="submission">
        <SectionTitle>Submission</SectionTitle>
        <SectionSubtitle>
          Send your submission via email with the required materials.
        </SectionSubtitle>
        <SubmissionSection>
          <SubFormat>
            <strong>Send to:</strong>
            <SubFormatCode>{submission.email}</SubFormatCode>
          </SubFormat>
          <SubFormat>
            <strong>Subject Format:</strong>
            <SubFormatCode>{submission.emailSubjectFormat.project1}</SubFormatCode>
            <span>or</span>
            <SubFormatCode>{submission.emailSubjectFormat.project2}</SubFormatCode>
          </SubFormat>
          <SubmissionBox>
            {submission.requirements.map((req) => (
              <div key={req.title}>
                <SubReqTitle>{req.title}</SubReqTitle>
                <SubReqDetail>{req.detail}</SubReqDetail>
              </div>
            ))}
          </SubmissionBox>
          <SubImportant>
            {submission.important}
          </SubImportant>
        </SubmissionSection>
      </Section>

      <Section id="roles">
        <SectionTitle>{roleEvaluation.title}</SectionTitle>
        <RoleDescription>{roleEvaluation.description}</RoleDescription>
        <RoleGrid>
          {roleEvaluation.roles.map((role) => (
            <RoleCard key={role.title}>
              <RoleTitle>{role.title}</RoleTitle>
              <RoleStack>{role.stack}</RoleStack>
              <RoleSignals>{role.signals}</RoleSignals>
            </RoleCard>
          ))}
        </RoleGrid>
      </Section>

      <RoleSection>
        <SectionSubtitle style={{ maxWidth: '600px', textAlign: 'center', margin: '0 auto', paddingBottom: '40px' }}>
          Good luck. Build something you're proud of.
        </SectionSubtitle>
      </RoleSection>
    </Page>
  );
}
